/**
 * Theme Storage Utilities
 * 
 * Simple localStorage wrapper for theme preferences without encryption.
 * Theme preferences are not sensitive data and don't require encryption.
 * This avoids issues with unstable encryption keys causing data loss.
 */

import { logger } from './logger';

type ThemePreference = 'light' | 'dark' | 'system';

interface ThemePreferences {
  byUser: Record<string, {
    colorScheme: ThemePreference;
    timestamp?: number;
  }>;
  lastUpdated?: number;
}

const STORAGE_KEY = 'wavescoop_theme_prefs';
const MIGRATION_KEY = 'wavescoop_theme_migration_done';

/**
 * ThemeStorage class for persistent theme preferences
 */
export class ThemeStorage {
  /**
   * Get all theme preferences
   */
  getPreferences(): ThemePreferences | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return null;
      }
      return JSON.parse(stored);
    } catch (error) {
      logger.warn('Failed to read theme preferences:', error);
      return null;
    }
  }

  /**
   * Get theme preference for a specific user
   */
  getUserPreference(userId: string): ThemePreference | null {
    try {
      const prefs = this.getPreferences();
      if (!prefs?.byUser?.[userId]) {
        return null;
      }
      return prefs.byUser[userId].colorScheme;
    } catch (error) {
      logger.warn('Failed to get user theme preference:', error);
      return null;
    }
  }

  /**
   * Set theme preference for a specific user
   */
  setUserPreference(userId: string, preference: ThemePreference): void {
    try {
      const existing = this.getPreferences() || { byUser: {} };
      
      existing.byUser[userId] = {
        colorScheme: preference,
        timestamp: Date.now()
      };
      existing.lastUpdated = Date.now();

      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
      
      // Log successful save in development
      if (process.env.NODE_ENV === 'development') {
        logger.info(`Theme preference saved: ${preference} for user ${userId}`);
      }
    } catch (error) {
      logger.error('Failed to save theme preference:', error);
      // Don't throw - theme is a non-critical preference
    }
  }

  /**
   * Clear all theme preferences
   */
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      logger.warn('Failed to clear theme preferences:', error);
    }
  }

  /**
   * Check if migration from encrypted storage has been done
   */
  isMigrationDone(): boolean {
    try {
      return localStorage.getItem(MIGRATION_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Mark migration as complete
   */
  markMigrationDone(): void {
    try {
      localStorage.setItem(MIGRATION_KEY, 'true');
    } catch (error) {
      logger.warn('Failed to mark migration done:', error);
    }
  }

  /**
   * Attempt to migrate from encrypted storage
   * This is a one-time migration for existing users
   */
  async migrateFromEncryptedStorage(): Promise<void> {
    // Only attempt migration once
    if (this.isMigrationDone()) {
      return;
    }

    try {
      // Try to import and read from secure storage
      const { getUserPreferences } = await import('./security/secureStorage');
      const encryptedPrefs = await getUserPreferences();
      
      if (encryptedPrefs?.byUser) {
        // Migrate each user's preferences
        const currentPrefs = this.getPreferences() || { byUser: {} };
        
        for (const [userId, userPrefs] of Object.entries(encryptedPrefs.byUser)) {
          if (userPrefs && typeof userPrefs === 'object' && 'colorScheme' in userPrefs) {
            // Only migrate if we don't already have a preference for this user
            if (!currentPrefs.byUser[userId]) {
              currentPrefs.byUser[userId] = {
                colorScheme: (userPrefs as any).colorScheme,
                timestamp: Date.now()
              };
              logger.info(`Migrated theme preference for user ${userId}`);
            }
          }
        }
        
        currentPrefs.lastUpdated = Date.now();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(currentPrefs));
      }
    } catch (error) {
      // Migration failure is not critical - just log it
      logger.warn('Theme migration from encrypted storage failed (non-critical):', error);
    } finally {
      // Mark migration as done regardless of success
      this.markMigrationDone();
    }
  }
}

// Export singleton instance
export const themeStorage = new ThemeStorage();

// Export for testing
export default {
  ThemeStorage,
  themeStorage
};